import type { Country } from "@/i18n/config"
import { isAllowedInCountry, type GateId } from "@/lib/country-gates"

/**
 * Which unit a calculator asks a lipid value in, per market.
 *
 * Every model on this site computes in mmol/L, because that is what the
 * papers and the Swiss laboratories both use. German laboratories report
 * lipids in mg/dL, so a German reader copying their own report into a mmol/L
 * box is the one mistake that produces a confidently wrong answer: 159 in a
 * field expecting 1.8 pushes a Fatty Liver Index over its upper cut-off and
 * prints "fat likely" over a clean liver.
 *
 * The calculator pages used to answer that with a refusal — no switcher, and
 * an out-of-range message explaining the conversion — on the reasoning that a
 * converter is the one place a reader can feed a number into a term expecting
 * a different scale. That reasoning holds where the local convention IS
 * mmol/L and a mg/dL entry is a mistake. It inverts in a market whose labs
 * print mg/dL: there the refusal fires on the correct value from the reader's
 * own report, and the only number they can enter is one they had to compute.
 *
 * So the unit is a market question, answered here, and the switch is offered
 * only where the market's labs disagree with the models. Two consequences
 * worth keeping in mind when adding a market:
 *
 *   - The gate is the registry entry, not a country literal. A market whose
 *     labs use mg/dL is added to `feature:tools-mgdl-lipids` and gets both the
 *     default and the switch from that one edit.
 *   - The switch is not a general-purpose converter. It changes which unit the
 *     reader is *asked for*, with the field's bounds, hint and mis-entry
 *     message all following it, which is what keeps a switched unit from
 *     becoming a silent scale error.
 */
export type LipidUnit = "mmolPerL" | "mgPerDl"

const GATE_ID = "feature:tools-mgdl-lipids" satisfies GateId

/**
 * The unit the form starts on for a reader in `country`.
 *
 * The local laboratory convention, so the common case is copying a number
 * across unchanged and the switch is for the exception.
 */
export function defaultLipidUnit(country: Country): LipidUnit {
  return isAllowedInCountry(GATE_ID, country) ? "mgPerDl" : "mmolPerL"
}

/**
 * Whether the form offers the unit switch at all.
 *
 * False in a market whose labs report mmol/L, where the field already speaks
 * the reader's unit and a switch would only add a way to get it wrong.
 */
export function offersLipidUnitSwitch(country: Country): boolean {
  return isAllowedInCountry(GATE_ID, country)
}

/** A reader's entry, in the mmol/L the models take. */
export function toMmolPerL(
  entry: number,
  unit: LipidUnit,
  mgDlPerMmolL: number
): number {
  return unit === "mgPerDl" ? entry / mgDlPerMmolL : entry
}

/** A mmol/L figure in the unit the reader is being asked for. */
export function fromMmolPerL(
  mmolPerL: number,
  unit: LipidUnit,
  mgDlPerMmolL: number
): number {
  return unit === "mgPerDl" ? mmolPerL * mgDlPerMmolL : mmolPerL
}

/**
 * True when the reader has mg/dL selected but typed a number only plausible
 * as mmol/L — 1.8 where 159 was meant.
 *
 * Deliberately one-directional. The mirror-image mistake, a mg/dL figure
 * typed while mmol/L is selected, is already caught by each model's own upper
 * bound on the value (`MAX_TRIGLYCERIDES` and friends), which is where it
 * belongs: that bound exists whether or not this module is involved. What no
 * model can see is this direction, because a mmol/L number read as mg/dL
 * converts to a value inside every plausible range — 1.8 becomes 0.02 mmol/L,
 * which is merely low rather than impossible — and would otherwise reach the
 * reader as "check your entries" over a value they copied correctly.
 */
export function looksLikeMmolPerLEntry(
  entry: number,
  unit: LipidUnit,
  minMmolPerL: number,
  mgDlPerMmolL: number
): boolean {
  if (unit !== "mgPerDl") return false
  return entry < minMmolPerL * mgDlPerMmolL
}
